// @ts-nocheck - untyped
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import IHubAdapterService from '../../../Services/IHubAdapterService';
import Dropzone from 'react-dropzone';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SendIcon from '@mui/icons-material/Send';
import DownloadIcon from '@mui/icons-material/Download';
import PreviewIcon from '@mui/icons-material/Preview';
import FilterListIcon from '@mui/icons-material/FilterList';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Notification from '../../Layout/Notification';
import { getCustomerDisplayName } from '../../../Services/CustomerMappings';
import '../../../App.scss';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

import './IHubAdapter.scss';

const ALLOWED_SERVICE_TYPES = ['I', 'M', 'R', 'V'];

const isValidUuid = value =>
  typeof value === 'string' &&
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(value);

const isValidWorkflowStatus = value => {
  const num = Number(value);
  return Number.isInteger(num) && num >= 0 && num <= 9;
};

const buildAnnotatedPreview = (data, issues = []) => {
  const index = {};
  issues.forEach(i => {
    const key = `${i.row}-${i.field}`;
    if (!index[key]) index[key] = [];
    index[key].push(i.message);
  });

  const lines = [];
  lines.push('[');
  data.forEach((service, idx) => {
    const row = idx + 1;
    lines.push('  {');
    const keys = Object.keys(service);
    keys.forEach((key, kIdx) => {
      const val = service[key];
      const jsonVal = JSON.stringify(val);
      const comma = kIdx < keys.length - 1 ? ',' : '';

      const issueKey = `${row}-${key}`;
      const msgs = index[issueKey];

      if (msgs && msgs.length) {
        const comment = ` // ${msgs.join(' | ')}`;
        lines.push(`    "${key}": ${jsonVal}${comma}${comment}`);
      } else {
        lines.push(`    "${key}": ${jsonVal}${comma}`);
      }
    });
    lines.push(idx < data.length - 1 ? '  },' : '  }');
  });
  lines.push(']');
  return lines.join('\n');
};

const CustomerMigrationUpload = ({ onJobStarted }) => {
  const [file, setFile] = useState(null);
  const [originalJsonData, setOriginalJsonData] = useState(null);
  const [jsonPreview, setJsonPreview] = useState(null);
  const [jsonValid, setJsonValid] = useState(null);
  const [annotatedPreview, setAnnotatedPreview] = useState('');
  const [loading, setLoading] = useState(false);

  const [customerNumber, setCustomerNumber] = useState('');
  const [customers, setCustomers] = useState({ data: [], isLoading: true });
  const [bulkSize, setBulkSize] = useState(10);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [migrationResult, setMigrationResult] = useState(null);
  const [progress, setProgress] = useState(0);
  const [currentBatch, setCurrentBatch] = useState(0);
  const [totalBatches, setTotalBatches] = useState(0);

  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const [customerMismatch, setCustomerMismatch] = useState(null);
  const [dateFilterStats, setDateFilterStats] = useState(null);

  useEffect(() => {
    let alive = true;

    async function fetchCustomers() {
      try {
        const response = await IHubAdapterService.getCustomers();
        const list = Array.isArray(response.data) ? response.data : [];
        if (alive) {
          setCustomers({ data: list, isLoading: false });
          if (!customerNumber && list.length) {
            setCustomerNumber(list[0].customerNumber);
          }
        }
      } catch (ex) {
        Notification.showError(`Failed to load customers: ${ex.message}`);
        if (alive) setCustomers({ data: [], isLoading: false });
      }
    }

    fetchCustomers();

    return () => {
      alive = false;
    };
  }, [customerNumber]);

  const validateJsonStructure = data => {
    if (!Array.isArray(data)) {
      return { valid: false, error: 'JSON must be an array of service objects', warnings: [] };
    }

    if (data.length === 0) {
      return { valid: false, error: 'JSON array is empty', warnings: [] };
    }

    const requiredFields = ['customerNumber', 'service_id', 'ip_service_case_id'];
    const missingFields = [];
    const warnings = [];

    for (let i = 0; i < data.length; i++) {
      const service = data[i];
      for (const field of requiredFields) {
        if (!service[field]) {
          missingFields.push(`Row ${i + 1}: missing field "${field}"`);
          break;
        }
      }
      if (missingFields.length >= 5) break;
    }

    if (missingFields.length > 0) {
      return {
        valid: false,
        error: `Invalid structure:\n${missingFields.join('\n')}${missingFields.length >= 5 ? '\n...' : ''}`,
        warnings,
      };
    }

    data.forEach((service, index) => {
      const row = index + 1;
      const serviceId = service.service_id;

      if (service.ip_service_type && !ALLOWED_SERVICE_TYPES.includes(service.ip_service_type)) {
        warnings.push({
          row,
          field: 'ip_service_type',
          serviceId,
          message: `Service Type property ${service.ip_service_type} is not valid. Allowed values are : I,M,R,V.`,
        });
      }

      if (!isValidWorkflowStatus(service.ip_service_status)) {
        warnings.push({
          row,
          field: 'ip_service_status',
          serviceId,
          message: `Status property ${service.ip_service_status} is not valid. Status code must be between 0-9.`,
        });
      }

      if (!isValidUuid(service.service_id)) {
        warnings.push({
          row,
          field: 'service_id',
          serviceId,
          message:
            'ServiceId format is not valid. ServiceId must not be empty and must be in UUID format (8-4-4-4-12  - total 36 characters).',
        });
      }

      if (!service.electronicReference) {
        warnings.push({
          row,
          field: 'electronicReference',
          serviceId,
          message:
            'Electronic Reference property is null in received Argos message, record will be skipped.',
        });
      }

      const dateFields = [
        'date_due',
        'date_lapse',
        'date_paid_IPO',
        'date_accepted_IPO',
        'date_payment_receipt',
        'date_official_receipt',
      ];
      dateFields.forEach(field => {
        const val = service[field];
        if (val && !/^\d{4}-\d{2}-\d{2}$/.test(val)) {
          warnings.push({
            row,
            field,
            serviceId,
            message: `Date "${field}" has invalid format "${val}". Expected format: YYYY-MM-DD.`,
          });
        } else if (val) {
          try {
            const dateObj = new Date(val);
            const minDate = new Date('1500-01-01');

            if (dateObj < minDate) {
              warnings.push({
                row,
                field,
                serviceId,
                message: `Date "${field}" value "${val}" is older than 1500-01-01 and will be rejected by iHub.`,
              });
            }
          } catch {
            warnings.push({
              row,
              field,
              serviceId,
              message: `Date "${field}" value "${val}" is not a valid date.`,
            });
          }
        }
      });
    });

    return { valid: true, count: data.length, warnings };
  };

  const getMigrationErrorMessage = error => {
    const data = error?.response?.data;

    if (data) {
      if (Array.isArray(data) && data.length > 0) {
        const first = data[0];
        const textArr = first.text;
        const refs = first.references;

        const rawText = Array.isArray(textArr) ? textArr.join(' ') : '';

        let coreMsg = rawText;
        const marker = 'IHubAdapter validation error:';
        if (rawText && rawText.includes(marker)) {
          coreMsg = rawText.substring(rawText.indexOf(marker) + marker.length).trim();
        }

        const serviceId =
          Array.isArray(refs) && refs.length > 0 && refs[0].service_id ? refs[0].service_id : null;

        if (serviceId && coreMsg) {
          return `${serviceId} - ${coreMsg}`;
        }

        if (rawText) return rawText;
      }

      if (Array.isArray(data.text)) return data.text.join(' ');
      if (typeof data === 'string') return data;
      if (typeof data.message === 'string') return data.message;

      return JSON.stringify(data);
    }

    return error?.message || 'Unknown error occurred';
  };

  const validateCustomerMatch = (jsonData, selectedCustomer) => {
    if (!jsonData || !selectedCustomer) return null;

    const mismatches = jsonData.filter(service => service.customerNumber !== selectedCustomer);

    if (mismatches.length > 0) {
      const uniqueMismatchCustomers = [...new Set(mismatches.map(s => s.customerNumber))];
      return {
        total: mismatches.length,
        customers: uniqueMismatchCustomers,
      };
    }

    return null;
  };

  const applyDateFilter = (jsonData, start, end) => {
    if (!start && !end) {
      return { filtered: jsonData, stats: null };
    }

    const filtered = jsonData.filter(service => {
      const dueDate = service.date_due;
      if (!dueDate) return true; // Keep services without date_due

      if (start && dueDate < start) return false;
      if (end && dueDate > end) return false;
      return true;
    });

    return {
      filtered,
      stats: {
        original: jsonData.length,
        filtered: filtered.length,
        removed: jsonData.length - filtered.length,
      },
    };
  };

  useEffect(() => {
    if (originalJsonData && customerNumber) {
      const mismatch = validateCustomerMatch(originalJsonData, customerNumber);
      setCustomerMismatch(mismatch);

      if (mismatch) {
        Notification.showWarn(`Services don't match selected customer!`);
      }
    }
  }, [originalJsonData, customerNumber]);

  useEffect(() => {
    if (originalJsonData && (startDate || endDate)) {
      const { stats } = applyDateFilter(originalJsonData, startDate, endDate);
      setDateFilterStats(stats);

      if (stats && stats.removed > 0) {
        Notification.showInfo(
          `Date filter will exclude ${stats.removed} service(s) from migration`
        );
      }
    } else {
      setDateFilterStats(null);
    }
  }, [originalJsonData, startDate, endDate]);

  const handleFileUpload = acceptedFiles => {
    if (!acceptedFiles || acceptedFiles.length === 0) return;

    const uploadedFile = acceptedFiles[0];
    setFile(uploadedFile);
    setMigrationResult(null);
    setJsonValid(null);
    setJsonPreview(null);
    setOriginalJsonData(null);
    setCustomerMismatch(null);
    setDateFilterStats(null);

    const reader = new FileReader();
    reader.onload = e => {
      try {
        const jsonData = JSON.parse(e.target.result);
        const validation = validateJsonStructure(jsonData);

        if (validation.valid) {
          setOriginalJsonData(jsonData);
          setJsonValid({ valid: true, count: validation.count });

          const annotated = buildAnnotatedPreview(jsonData, validation.warnings || []);
          setAnnotatedPreview(annotated);
          setJsonPreview(jsonData);

          const warningsCount = validation.warnings?.length || 0;

          if (warningsCount > 0) {
            Notification.showWarn(
              `${warningsCount} validation warning(s). Check JSON preview panel below.`
            );
          } else {
            Notification.showSuccess(`Valid JSON with ${validation.count} service(s) loaded`);
          }

          if (customerNumber) {
            const mismatch = validateCustomerMatch(jsonData, customerNumber);
            setCustomerMismatch(mismatch);
          }
        } else {
          setJsonValid({ valid: false, error: validation.error });
          setAnnotatedPreview('');
          Notification.showError(validation.error);
        }
      } catch (err) {
        setJsonValid({ valid: false, error: 'Invalid JSON format' });
        Notification.showError('Invalid JSON format: ' + err.message);
      }
    };
    reader.readAsText(uploadedFile);
  };

  const clearFile = evt => {
    if (evt) {
      evt.stopPropagation();
      evt.preventDefault();
    }
    setFile(null);
    setMigrationResult(null);
    setJsonValid(null);
    setJsonPreview(null);
    setAnnotatedPreview('');
    setOriginalJsonData(null);
    setCustomerMismatch(null);
    setDateFilterStats(null);
    setProgress(0);
    setCurrentBatch(0);
    setTotalBatches(0);
  };

  const downloadTemplate = () => {
    const template = [
      {
        customerNumber: '89.0004',
        service_id: '131e0aeb-e3f2-464d-9455-a10ec8e07624',
        parent_type: 'Design',
        ip_service_case_id: 'PP015346CN8',
        electronicReference: '359326',
        ip_service_type: 'M',
        ip_service_subtype: 'Annuity 11',
        ip_service_status: 1,
        ip_service_number: 'PP0093819876',
        ip_service_agent: 'Test',
        ip_service_annuity: 11,
        date_due: '2020-01-01',
        date_lapse: '2020-01-01',
        date_paid_IPO: '2020-01-01',
        date_accepted_IPO: '2020-01-01',
        date_payment_receipt: '2020-01-01',
        date_official_receipt: '2020-01-01',
        client_instructions: null,
        cost_currency_code: 'EUR',
        cost_total: null,
        cost_official_subtotal: 500,
        cost_agent_subtotal: null,
        cost_admin_subtotal: null,
      },
    ];

    const dataStr = JSON.stringify(template, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'services_template.json';
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleSubmit = async evt => {
    evt.preventDefault();
    if (!file || !customerNumber || !jsonValid?.valid || !originalJsonData || customerMismatch)
      return;

    setLoading(true);
    setProgress(0);
    setMigrationResult(null);

    try {
      let dataToSend = originalJsonData;
      if (startDate || endDate) {
        const { filtered, stats } = applyDateFilter(originalJsonData, startDate, endDate);
        dataToSend = filtered;

        if (stats.removed > 0) {
          Notification.showInfo(
            ` Filtered ${stats.removed} service(s) by date range (${stats.filtered} remaining)`
          );
        }
      }

      if (dataToSend.length === 0) {
        throw new Error('No services to migrate after applying filters!');
      }

      const filteredJson = JSON.stringify(dataToSend);
      const filteredBlob = new Blob([filteredJson], { type: 'application/json' });
      const filteredFile = new File([filteredBlob], file.name, { type: 'application/json' });

      const totalServices = dataToSend.length;
      const batches = Math.ceil(totalServices / bulkSize);
      setTotalBatches(batches);

      const progressInterval = setInterval(() => {
        setCurrentBatch(prev => {
          const next = prev + 1;
          if (next >= batches) {
            clearInterval(progressInterval);
            return batches;
          }
          setProgress(Math.floor((next / batches) * 90));
          return next;
        });
      }, 800);

      const request = { file: filteredFile };
      const response = await IHubAdapterService.migrateServicesInBulk(request, bulkSize);

      clearInterval(progressInterval);
      setProgress(100);
      setCurrentBatch(batches);

      if (response.status === 201) {
        const resultData = response.data;
        setMigrationResult({
          success: true,
          data: resultData,
        });

        const successCount = resultData?.data?.filter(item => item.uin).length || 0;
        const totalCount = resultData?.data?.length || 0;

        Notification.showSuccess(
          `Migration completed! ${successCount}/${totalCount} services processed successfully.`
        );
        if (onJobStarted) onJobStarted(customerNumber);
      } else if (response.status === 204) {
        setMigrationResult({
          success: false,
          message: 'No entries were sent to iHub',
        });
        Notification.showWarn('No entries were sent to iHub');
      }
    } catch (error) {
      setProgress(0);
      setCurrentBatch(0);

      const errorMsg = getMigrationErrorMessage(error);

      setMigrationResult({
        success: false,
        error: errorMsg,
      });
      Notification.showWarn('Some services failed validation. Check JSON Preview panel.');
    } finally {
      setLoading(false);
    }
  };

  const exportResults = () => {
    if (!migrationResult?.data?.data) return;

    const data = migrationResult.data.data;
    const csv = [
      ['Reference', 'Status', 'Message'].join(','),
      ...data.map(item =>
        [
          item.reference || item.id || '',
          item.status || '',
          (item.message || item.description || '').replace(/,/g, ';'),
        ].join(',')
      ),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `migration_results_${new Date().toISOString()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const renderJsonPreview = () => {
    if (!originalJsonData) return null;

    const previewText = annotatedPreview || JSON.stringify(originalJsonData, null, 2);
    const previewLines = previewText.split('\n');

    return (
      <Card variant="outlined" className="ihub-preview-card">
        <CardContent>
          <Stack direction="row" justifyContent="space-between" className="ihub-preview-header">
            <Typography variant="subtitle2" fontWeight="bold">
              <PreviewIcon fontSize="small" />
              JSON Preview
            </Typography>
            <Chip
              label={`${jsonValid?.count || 0} total services`}
              size="small"
              color="primary"
              variant="outlined"
            />
          </Stack>
          <Paper variant="outlined" className="ihub-preview-paper">
            <pre className="ihub-preview-pre">
              {previewLines.map((line, idx) => (
                <div
                  key={idx}
                  style={{
                    backgroundColor: line.includes('//') ? 'var(--t-danger-bg)' : 'transparent',
                  }}
                >
                  {line}
                </div>
              ))}
            </pre>
          </Paper>
        </CardContent>
      </Card>
    );
  };

  const renderResultTable = () => {
    if (!migrationResult?.data?.data) return null;

    const allData = migrationResult.data.data;
    const messages = migrationResult.data.messages || [];

    const filteredData = allData.filter(item => {
      if (statusFilter !== 'all') {
        const isSuccess = item.status === 'SUCCESS' || item.success;
        if (statusFilter === 'success' && !isSuccess) return false;
        if (statusFilter === 'error' && isSuccess) return false;
      }
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        return (
          (item.reference || '').toLowerCase().includes(search) ||
          (item.recordId || '').toLowerCase().includes(search) ||
          (item.uin || '').toLowerCase().includes(search)
        );
      }
      return true;
    });

    const successCount = allData.filter(item => item.status === 'SUCCESS' || item.success).length;
    const errorCount = allData.length - successCount;

    return (
      <Card variant="outlined" className="ihub-result-card">
        <CardContent>
          <Card className="ihub-stats-card">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Migration Results
              </Typography>
              <Grid container spacing={2}>
                <Grid size={4}>
                  <Typography variant="h4">{allData.length}</Typography>
                  <Typography variant="caption">Total Processed</Typography>
                </Grid>
                <Grid size={4}>
                  <Typography variant="h4" color="success.light">
                    {successCount}
                  </Typography>
                  <Typography variant="caption">Successful</Typography>
                </Grid>
                <Grid size={4}>
                  <Typography variant="h4" color="error.light">
                    {errorCount}
                  </Typography>
                  <Typography variant="caption">Failed</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {messages && messages.length > 0 && (
            <Alert severity="warning" className="ihub-result-alert-mb">
              <Typography variant="body2" fontWeight="bold" gutterBottom>
                Messages from iHub:
              </Typography>
              {messages.map((msg, idx) => (
                <Typography key={idx} variant="caption" component="div">
                  • {typeof msg === 'string' ? msg : JSON.stringify(msg)}
                </Typography>
              ))}
            </Alert>
          )}

          <Stack direction="row" spacing={1} className="ihub-result-filters">
            <FormControl size="small" className="ihub-result-status-select">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={e => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All ({allData.length})</MenuItem>
                <MenuItem value="success">Success ({successCount})</MenuItem>
                <MenuItem value="error">Error ({errorCount})</MenuItem>
              </Select>
            </FormControl>
            <TextField
              size="small"
              placeholder="Search by reference or UIN..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="ihub-result-search"
            />
            <Button
              size="small"
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={exportResults}
            >
              Export CSV
            </Button>
          </Stack>

          <TableContainer component={Paper} variant="outlined" className="ihub-result-table">
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Status</TableCell>
                  <TableCell>Record ID</TableCell>
                  <TableCell>UIN</TableCell>
                  <TableCell>Reference</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        className="ihub-result-empty"
                      >
                        No results match the current filters
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredData.map((item, idx) => {
                    const isSuccess = item.status === 'SUCCESS' || item.success;
                    return (
                      <TableRow
                        key={idx}
                        className={isSuccess ? 'ihub-success-row' : 'ihub-error-row'}
                      >
                        <TableCell>
                          {isSuccess ? (
                            <Chip
                              icon={<CheckCircleIcon />}
                              label="Success"
                              size="small"
                              color="success"
                            />
                          ) : (
                            <Chip
                              icon={<ErrorOutlineIcon />}
                              label="Error"
                              size="small"
                              color="error"
                            />
                          )}
                        </TableCell>
                        <TableCell>{item.recordId || '-'}</TableCell>
                        <TableCell>
                          <Typography variant="caption" className="ihub-result-uin">
                            {item.uin || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>{item.reference || '-'}</TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    );
  };

  return (
    <div id="ihub-adapter-upload-container">
      <div className="ipxc-admin-body">
        <form onSubmit={handleSubmit} autoComplete="off">
          <Grid container spacing={2} alignItems="stretch">
            <Grid size={{ xs: 12, md: 6 }}>
              <Card variant="outlined" className="ihub-upload-card">
                <CardHeader
                  title="Start a bulk migration"
                  subheader="Pick a client and upload your .json file"
                  titleTypographyProps={{ variant: 'h4', fontWeight: 'bold' }}
                  subheaderTypographyProps={{ variant: 'body2' }}
                />
                <CardContent>
                  <FormControl fullWidth size="small" className="ihub-form-mb">
                    <InputLabel>Customer</InputLabel>
                    <Select
                      value={customerNumber}
                      label="Customer"
                      onChange={e => setCustomerNumber(e.target.value)}
                      disabled={loading || customers.isLoading}
                    >
                      {customers.data.map(cust => (
                        <MenuItem key={cust.customerNumber} value={cust.customerNumber}>
                          {getCustomerDisplayName(cust.customerNumber, cust.name)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {customerMismatch && (
                    <Alert fullWidth severity="error" className="ihub-alert-center">
                      <Typography variant="body2" fontWeight="bold">
                        Customer Mismatch Detected - Migration Blocked!
                      </Typography>
                      <Typography variant="caption">
                        JSON have different customerNumber:
                        <br />
                        {customerMismatch.customers.join(', ')}
                        <br />
                        Please ensure all services match the selected customer before starting
                        migration.
                      </Typography>
                    </Alert>
                  )}

                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Bulk Size"
                    value={bulkSize}
                    onChange={e => {
                      const val = parseInt(e.target.value) || 1;
                      setBulkSize(Math.max(1, Math.min(val, 10)));
                    }}
                    inputProps={{ min: 1, max: 10 }}
                    helperText={
                      bulkSize > 10
                        ? 'Bulk size above 10 not recommended! iHub may not handle it properly.'
                        : 'Number of services per batch (recommended: 1-10)'
                    }
                    error={bulkSize > 10}
                    disabled={loading}
                    className="ihub-form-mb"
                  />

                  <Typography variant="subtitle2" className="ihub-subtitle-mb">
                    <FilterListIcon fontSize="small" className="ihub-filter-icon" />
                    Date Range Filter (Optional)
                  </Typography>
                  <Stack direction="row" spacing={1} className="ihub-form-mb">
                    <TextField
                      size="small"
                      type="date"
                      label="Start Date"
                      value={startDate}
                      onChange={e => setStartDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      disabled={loading}
                      className="ihub-date-field"
                    />
                    <TextField
                      size="small"
                      type="date"
                      label="End Date"
                      value={endDate}
                      onChange={e => setEndDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      disabled={loading}
                      className="ihub-date-field"
                    />
                  </Stack>

                  {dateFilterStats && dateFilterStats.removed > 0 && (
                    <Alert
                      severity={dateFilterStats.filtered === 0 ? 'error' : 'info'}
                      className="ihub-form-mb"
                    >
                      <Typography variant="caption">
                        {dateFilterStats.filtered === 0 ? (
                          <>
                            <strong>No services match the date range!</strong>
                            <br />
                            All {dateFilterStats.original} service(s) are excluded by the selected
                            date range.
                            <br />
                            Migration is blocked. Please adjust the date range or remove filters.
                          </>
                        ) : (
                          <>
                            Date filter will process {dateFilterStats.filtered} out of{' '}
                            {dateFilterStats.original} services
                            <br />({dateFilterStats.removed} excluded by date range)
                          </>
                        )}
                      </Typography>
                    </Alert>
                  )}

                  <div className="ihub-dropzone-wrapper">
                    <Typography variant="subtitle2" className="ihub-subtitle-mb">
                      Upload JSON File
                    </Typography>
                    <Dropzone
                      onDrop={handleFileUpload}
                      accept={{ 'application/json': ['.json'] }}
                      multiple={false}
                      disabled={loading}
                    >
                      {({ getRootProps, getInputProps, isDragActive }) => (
                        <div
                          {...getRootProps()}
                          className={`ihub-dropzone ${isDragActive ? 'ihub-dropzone-active' : ''}`}
                        >
                          <input {...getInputProps()} />
                          <Typography variant="body2" className="ihub-dropzone-hint">
                            {isDragActive
                              ? 'Drop JSON file here...'
                              : 'Drag & drop JSON file here, or click to browse'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Only JSON (.json) files are accepted
                          </Typography>
                          <CloudUploadIcon className="ihub-upload-icon" />
                          {file && (
                            <div className="ihub-filename-wrap">
                              <ListItemText
                                className="ihub-file-label"
                                primary={file.name}
                                secondary={
                                  jsonValid?.valid
                                    ? `Valid - ${jsonValid.count} service(s)`
                                    : jsonValid?.error
                                      ? `${jsonValid.error}`
                                      : 'Validating...'
                                }
                              />
                              <Tooltip title="Clear file">
                                <IconButton size="small" onClick={clearFile} disabled={loading}>
                                  <ClearIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </div>
                          )}
                        </div>
                      )}
                    </Dropzone>

                    {jsonValid && !jsonValid.valid && (
                      <Alert severity="error" className="ihub-alert-mt">
                        <Typography variant="caption" className="ihub-error-preline">
                          {jsonValid.error}
                        </Typography>
                      </Alert>
                    )}
                  </div>

                  {loading && (
                    <div className="ihub-progress-box">
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        className="ihub-progress-stack"
                      >
                        <CircularProgress size={20} />
                        <Typography variant="body2" color="text.secondary">
                          Processing migration... Batch {currentBatch} of {totalBatches}
                        </Typography>
                      </Stack>
                      <LinearProgress variant="determinate" value={progress} />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        className="ihub-progress-pct"
                      >
                        {progress}% complete
                      </Typography>
                    </div>
                  )}

                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Button
                      size="small"
                      variant="text"
                      startIcon={<DownloadIcon />}
                      onClick={downloadTemplate}
                      disabled={loading}
                    >
                      Download Template
                    </Button>
                    <Button
                      id="ihub-adapter-start-button"
                      type="submit"
                      variant="contained"
                      disabled={
                        !file ||
                        !customerNumber ||
                        !jsonValid?.valid ||
                        loading ||
                        customerMismatch ||
                        (dateFilterStats && dateFilterStats.filtered === 0)
                      }
                      startIcon={loading ? <CircularProgress size={16} /> : <SendIcon />}
                    >
                      {loading ? 'Processing...' : 'Start Migration'}
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Card variant="outlined" className="ihub-side-card">
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                    Process Information
                  </Typography>
                  <Stack spacing={1}>
                    <Alert severity="info" icon={<InfoOutlinedIcon />}>
                      <Typography variant="body2">
                        <strong>Bulk Size:</strong> Number of services processed per batch.
                        Recommended: 1-10 for optimal performance. Max: 10.
                      </Typography>
                    </Alert>
                    <Alert severity="info" icon={<InfoOutlinedIcon />}>
                      <Typography variant="body2">
                        <strong>Date Range:</strong> Optional filters to process only services
                        within specific date range (based on due_date field).
                      </Typography>
                    </Alert>
                    <Alert severity="warning" icon={<WarningAmberIcon />}>
                      <Typography variant="body2">
                        <strong>Important:</strong> System will validate that all service records
                        match the selected customer. Services with mismatched customerNumber will
                        block the migration process until corrected.
                      </Typography>
                    </Alert>
                  </Stack>

                  <Divider className="ihub-divider-my" />
                  <Accordion disableGutters>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <InfoOutlinedIcon color="info" className="ihub-accordion-info-icon" />
                      <Typography variant="subtitle2">JSON Structure Example</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Paper variant="outlined" className="ihub-json-example">
                        <pre className="ihub-json-pre">
                          {`[
  {
    "customerNumber": "89.0004",
    "service_id": "131e0aeb-...",
    "parent_type": "Design",
    "ip_service_case_id": "PP015346CN8",
    "electronicReference": "359326",
    "ip_service_type": "M",
    "ip_service_subtype": "Annuity 11",
    "ip_service_status": 1,
    "ip_service_number": "PP0093819876",
    "date_due": "2020-01-01",
    "cost_currency_code": "EUR",
    "cost_official_subtotal": 500
  }
]`}
                        </pre>
                      </Paper>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        className="ihub-json-notes"
                      >
                        • Array of service objects
                        <br />
                        • Valid JSON format required
                        <br />
                        • Date format: YYYY-MM-DD
                        <br />• All services must belong to selected customer
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                  <Divider className="ihub-divider-my" />
                  <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                    Tips for Best Results
                  </Typography>
                  <Typography variant="body2" component="div">
                    <ul className="ihub-tips-list">
                      <li>Validate JSON structure before upload using the preview</li>
                      <li>Ensure all services match the selected customer</li>
                      <li>Use date range filters for large datasets</li>
                      <li>Start with bulk size of 1-10</li>
                      <li>Export results after migration for record keeping</li>
                      <li>Review warnings before confirming migration</li>
                    </ul>
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            {jsonPreview && <Grid size={12}>{renderJsonPreview()}</Grid>}
            {migrationResult && <Grid size={12}>{renderResultTable()}</Grid>}
          </Grid>
        </form>
      </div>
    </div>
  );
};

CustomerMigrationUpload.propTypes = {
  onJobStarted: PropTypes.func,
};

export default CustomerMigrationUpload;
